/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Inject, Injectable} from '@angular/core';
import * as _ from 'lodash';

import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {LibraryCreativeResource} from '../../components/api/resources/resource/library-creative.resource';
import {LineItemResource} from '../../components/api/resources/resource/line-item.resource';
import {OrderResource} from '../../components/api/resources/resource/order.resource';
import {CreativeSetInfoResource} from '../../components/api/resources/resource/creative-set.resource';
import {FolioResource} from '../../components/api/resources/resource/folio.resource';
import {
  advertiserRefFromRelated,
  advertiserRefFromWarning,
  asAdvertiserRef,
  creativeIdsFromWarning,
  findCreativeAdvertiserMismatch,
  findFolioCreativeAdvertisersMismatch,
  findOrderCreativeAdvertisersMismatch,
  isFolioAdvertiserMismatchWarning,
  isOrderAdvertiserMismatchWarning
} from './creative-advertiser-mismatch';

@Injectable({providedIn: 'root'})
export class CreativeAdvertiserMismatchService {
  constructor(
    @Inject(CreativeResource) private readonly Creative: any,
    @Inject(LibraryCreativeResource) private readonly LibraryCreative: any,
    @Inject(LineItemResource) private readonly LineItem: any,
    @Inject(OrderResource) private readonly Order: any,
    @Inject(CreativeSetInfoResource) private readonly CreativeSetInfo: any,
    @Inject(FolioResource) private readonly Folio: any
  ) {
  }

  findMismatch(warnings: any): any {
    return findCreativeAdvertiserMismatch(warnings);
  }

  updateAdvertiser(creative: any, options?: {libraryCreative?: boolean}): Promise<any> {
    if (!creative?.id) {
      return Promise.reject(new Error('Creative id is required'));
    }
    const Resource = options?.libraryCreative ? this.LibraryCreative : this.Creative;
    return this.resolveExpectedAdvertiser(creative, Resource).then((advertiser) => {
      if (!advertiser?.id) {
        return Promise.reject(new Error('Expected advertiser could not be resolved'));
      }
      return new Resource({
        id: creative.id,
        advertiser: {id: advertiser.id}
      }).savePartial(['advertiser']);
    }).then((saved: any) => {
      if (creative) {
        creative.advertiser = saved.advertiser;
        _.set(creative, 'validationWarnings', saved.validationWarnings);
      }
      return saved;
    });
  }

  updateOrderCreativesAdvertiser(order: any): Promise<any> {
    return this.updateParentCreativesAdvertiser(order, 'order');
  }

  updateFolioCreativesAdvertiser(folio: any): Promise<any> {
    return this.updateParentCreativesAdvertiser(folio, 'folio');
  }

  private updateParentCreativesAdvertiser(parent: any, kind: 'order' | 'folio'): Promise<any> {
    if (!parent?.id) {
      return Promise.reject(new Error(kind === 'folio' ? 'Folio id is required' : 'Order id is required'));
    }
    const ParentResource = kind === 'folio' ? this.Folio : this.Order;
    const knownAdvertiser = asAdvertiserRef(parent.advertiser);
    const advertiserPromise = knownAdvertiser
      ? Promise.resolve(knownAdvertiser)
      : kind === 'folio'
        ? this.Folio.get({id: parent.id}).$promise.then((fresh: any) => asAdvertiserRef(fresh.advertiser))
        : this.Order.getDataView({id: parent.id}).then((fresh: any) => asAdvertiserRef(fresh.advertiser));
    return advertiserPromise.then((advertiser) => {
      if (!advertiser?.id) {
        return Promise.reject(new Error(kind === 'folio'
          ? 'Folio advertiser could not be resolved'
          : 'Order advertiser could not be resolved'));
      }
      return this.affectedCreativeIds(parent, kind).then((ids) => {
        if (!ids.length) {
          return this.refreshParentWarnings(parent, ParentResource);
        }
        const payload = _.map(ids, (id: string) => {
          return {id: id, advertiser: {id: advertiser.id}};
        });
        return this.Creative.bulkSave(payload).$promise.then(() => {
          return this.refreshParentWarnings(parent, ParentResource);
        });
      });
    });
  }

  private affectedCreativeIds(parent: any, kind: 'order' | 'folio'): Promise<string[]> {
    const mismatch = kind === 'folio'
      ? findFolioCreativeAdvertisersMismatch(parent.validationWarnings)
      : findOrderCreativeAdvertisersMismatch(parent.validationWarnings);
    const fromWarning = creativeIdsFromWarning(mismatch);
    if (fromWarning.length) {
      return Promise.resolve(fromWarning);
    }
    const parentAdvertiserId = _.get(parent, ['advertiser', 'id']);
    const query = kind === 'folio'
      ? {folio: parent.id, pageSize: 1001}
      : {order: parent.id, pageSize: 1001};
    return this.Creative.query(query).$promise.then((page: any) => {
      return _.map(_.filter(page.results || [], (creative: any) => {
        if (findCreativeAdvertiserMismatch(creative.validationWarnings)) {
          return true;
        }
        const creativeAdvertiserId = _.get(creative, ['advertiser', 'id']);
        return !!parentAdvertiserId && !!creativeAdvertiserId && creativeAdvertiserId !== parentAdvertiserId;
      }), 'id');
    });
  }

  private refreshParentWarnings(parent: any, ParentResource: any): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(resolve, 1000);
    }).then(() => {
      return new ParentResource({id: parent.id}).savePartial(['id']).then((fresh: any) => {
        _.set(parent, 'validationWarnings', fresh.validationWarnings);
        if (fresh.advertiser) {
          parent.advertiser = fresh.advertiser;
        }
        return fresh;
      });
    });
  }

  private resolveExpectedAdvertiser(creative: any, Resource: any): Promise<{id: string; name?: string} | null> {
    const warning = findCreativeAdvertiserMismatch(creative.validationWarnings);
    const fromWarning = advertiserRefFromWarning(warning);
    if (fromWarning) {
      return Promise.resolve(fromWarning);
    }
    const fromRelated = advertiserRefFromRelated(creative);
    if (fromRelated) {
      return Promise.resolve(fromRelated);
    }
    return this.ensureParentRefs(creative, Resource).then(() => {
      const afterHydrate = advertiserRefFromRelated(creative);
      if (afterHydrate) {
        return afterHydrate;
      }
      if (isOrderAdvertiserMismatchWarning(warning) || _.get(creative, ['lineItem', 'id'])) {
        return this.advertiserFromOrder(creative);
      }
      if (isFolioAdvertiserMismatchWarning(warning) || _.get(creative, ['creativeSet', 'id']) || _.get(creative, ['folio', 'id'])) {
        return this.advertiserFromFolio(creative);
      }
      return null;
    });
  }

  private ensureParentRefs(creative: any, Resource: any): Promise<void> {
    if (_.get(creative, ['lineItem', 'id']) || _.get(creative, ['creativeSet', 'id']) || _.get(creative, ['folio', 'id'])) {
      return Promise.resolve();
    }
    return Resource.get({id: creative.id}).$promise.then((fresh: any) => {
      if (_.get(fresh, ['lineItem', 'id'])) {
        creative.lineItem = creative.lineItem || fresh.lineItem;
      }
      if (_.get(fresh, ['creativeSet', 'id'])) {
        creative.creativeSet = creative.creativeSet || fresh.creativeSet;
      }
      if (_.get(fresh, ['folio', 'id'])) {
        creative.folio = creative.folio || fresh.folio;
      }
      if (!creative.validationWarnings && fresh.validationWarnings) {
        creative.validationWarnings = fresh.validationWarnings;
      }
    });
  }

  private advertiserFromOrder(creative: any): Promise<{id: string; name?: string} | null> {
    const lineItemId = _.get(creative, ['lineItem', 'id']);
    if (!lineItemId) {
      return Promise.resolve(null);
    }
    return this.LineItem.get({id: lineItemId}).$promise.then((lineItem: any) => {
      const fromLineItem = asAdvertiserRef(lineItem.advertiser) || asAdvertiserRef(_.get(lineItem, ['order', 'advertiser']));
      if (fromLineItem) {
        return fromLineItem;
      }
      const orderId = _.get(lineItem, ['order', 'id']);
      if (!orderId) {
        return null;
      }
      return this.Order.getDataView({id: orderId}).then((order: any) => {
        return asAdvertiserRef(order.advertiser);
      });
    });
  }

  private advertiserFromFolio(creative: any): Promise<{id: string; name?: string} | null> {
    const creativeSetId = _.get(creative, ['creativeSet', 'id']);
    const folioId = _.get(creative, ['folio', 'id']) || _.get(creative, ['creativeSet', 'folio', 'id']);
    const fromInfo = creativeSetId
      ? this.CreativeSetInfo.get({id: creativeSetId}).$promise.then((cs: any) => {
        return asAdvertiserRef(cs.advertiser) || asAdvertiserRef(_.get(cs, ['folio', 'advertiser']));
      })
      : Promise.resolve(null);
    return fromInfo.then((advertiser) => {
      if (advertiser || !folioId) {
        return advertiser;
      }
      return this.Folio.get({id: folioId}).$promise.then((folio: any) => {
        return asAdvertiserRef(folio.advertiser);
      });
    });
  }
}
